
'use client';

import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/auth-context';
import { useToast } from '@/hooks/use-toast';
import { Mail, LogOut, Loader2, RefreshCw } from 'lucide-react';
import { Icons } from './icons';

export default function VerifyEmailGate() {
    const { user, logout, resendVerificationEmail, reloadUser, loading } = useAuth();
    const { toast } = useToast();

    const handleResendEmail = async () => {
        try {
            await resendVerificationEmail();
            toast({
                title: "Verification Email Sent",
                description: "A new verification email has been sent to your address. Please check your inbox (and spam folder).",
            });
        } catch (error: any) {
            toast({
                title: "Error Sending Email",
                description: error.message || "Could not send verification email. Please try again later.",
                variant: "destructive",
            });
        }
    };
    
    const handleCheckVerification = async () => {
        await reloadUser();
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-background p-4">
            <Card className="w-full max-w-md text-center fade-in-up">
                <CardHeader>
                    <div className="mx-auto bg-primary/20 text-primary rounded-full p-4 w-fit mb-4">
                        <Mail className="w-10 h-10" />
                    </div>
                    <CardTitle className="text-2xl font-bold">Verify Your Email</CardTitle>
                    <CardDescription>
                        A verification link has been sent to <span className="font-semibold text-primary">{user?.email}</span>. Please check your inbox to continue.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-col gap-2">
                        <Button onClick={handleResendEmail} disabled={loading}>
                            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Resend Verification Email
                        </Button>
                         <Button onClick={handleCheckVerification} variant="secondary" disabled={loading}>
                            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                            I've Verified, Check Again
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Can't find the email? Check your spam or junk folder.
                    </p>
                    <Button variant="link" onClick={logout} className="text-muted-foreground">
                        <LogOut className="mr-2 h-4 w-4"/>
                        Logout
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}
